/* eslint-env browser, es2020 */
/* global BigInt, OffscreenCanvas, createImageBitmap */

// utils/selection.js (v7)
// ------------------------------------------------------------
// What this provides (drop-in for src/utils/selection.js):
// 1) Strict vibe filtering (people/scenery/activity/highlight) with knobs
// 2) Likes/Includes/Excludes (likes override filters)
// 3) Stronger de-duplication:
//    - perceptual dHash (64-bit) + RGB histogram (16x3 bins)
//    - time-burst dedupe (drop near-duplicates within N seconds)
//    - hard no-near-duplicate rule inside MMR (hardDedupThreshold)
// 4) Diversity via MMR with a per-scene cap
// 5) Semi-auto and full-auto flows + album JSON export helper
//    (semiAutoCandidates, finalizeSemiAuto, selectAlbumPhotos)
// 6) Backward-compatible function signatures; new options are optional.
// ------------------------------------------------------------

/* ---------------------------- small helpers ---------------------------- */

function clamp01(x) { return Math.max(0, Math.min(1, x)); }

function canvas2D(w, h) {
  if (typeof OffscreenCanvas === 'function') {
    const c = new OffscreenCanvas(w, h);
    const ctx = c.getContext('2d', { willReadFrequently: true });
    return { c, ctx };
  }
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  return { c, ctx };
}

export function stableUid(fileOrPhoto, idx = 0) {
  if (fileOrPhoto?.id) return String(fileOrPhoto.id);
  const name = (fileOrPhoto?.name || fileOrPhoto?.file?.name || fileOrPhoto?.url || '') + '';
  const size = fileOrPhoto?.size ?? fileOrPhoto?.file?.size ?? 0;
  const lm = fileOrPhoto?.lastModified ?? fileOrPhoto?.file?.lastModified ?? 0;
  return `${name}|${size}|${lm}|${idx}`;
}

function getTimestampSec(p) {
  // Try a few fields we might have; fall back to File.lastModified
  const raw = p?.takenAt || p?.meta?.exif?.DateTimeOriginal || p?.file?.lastModified || p?.lastModified || 0;
  if (typeof raw === 'string') {
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? Math.round(ms / 1000) : 0;
  }
  if (typeof raw === 'number') return Math.round(raw / 1000);
  return 0;
}

/* --------------------------- perceptual hashes ------------------------- */

function dHashFromBitmap(bmp) {
  const w = 9, h = 8;
  const { c, ctx } = canvas2D(w, h);
  ctx.drawImage(bmp, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  let hash = 0n, bit = 0n;
  const grayAt = (i) => (data[i]*0.299 + data[i+1]*0.587 + data[i+2]*0.114);
  for (let y=0;y<h;y++) for (let x=0;x<w-1;x++) {
    const i1 = (y*w + x) * 4;
    const i2 = (y*w + x + 1) * 4;
    const v = grayAt(i1) < grayAt(i2) ? 1n : 0n;
    hash |= (v << bit);
    bit += 1n;
  }
  return hash; // 64 bits
}
function hamming64(a, b) { let x=a^b, c=0; while (x) { c += Number(x & 1n); x >>= 1n; } return c; }

function rgbHistFromBitmap(bmp) {
  const binsPer = 16, W = 96, H = 96;
  const { c, ctx } = canvas2D(W, H);
  ctx.drawImage(bmp, 0, 0, W, H);
  const { data } = ctx.getImageData(0, 0, W, H);
  const hist = new Float32Array(binsPer * 3);
  const scale = 256 / binsPer;
  for (let i=0;i<data.length;i+=4) {
    const r = Math.min(binsPer-1, (data[i]   / scale) | 0);
    const g = Math.min(binsPer-1, (data[i+1] / scale) | 0);
    const b = Math.min(binsPer-1, (data[i+2] / scale) | 0);
    hist[r] += 1;
    hist[binsPer + g] += 1;
    hist[2*binsPer + b] += 1;
  }
  // L2 normalize
  let n=0; for (let i=0;i<hist.length;i++) n += hist[i]*hist[i];
  n = Math.sqrt(n) || 1;
  for (let i=0;i<hist.length;i++) hist[i] /= n;
  return hist;
}

async function toBitmap(blobOrUrl) {
  if (blobOrUrl instanceof Blob) return createImageBitmap(blobOrUrl);
  const blob = await fetch(blobOrUrl).then(r => r.blob());
  return createImageBitmap(blob);
}

/* ----------------------------- signatures ----------------------------- */

export async function ensureSignatures(photos, concurrency = 4) {
  const queue = [...photos];
  const workers = new Array(Math.min(concurrency, queue.length)).fill(0).map(async () => {
    while (queue.length) {
      const p = queue.shift(); if (!p) break;
      if (p.meta?.sign?.dhash && p.meta?.sign?.hist) continue;
      const src = p.blob || p._blob || p.url || (p.file && p.file);
      if (!src) continue;
      try {
        const bmp = await toBitmap(src);
        const dh = dHashFromBitmap(bmp);
        const hh = rgbHistFromBitmap(bmp);
        p.meta = p.meta || {}; p.meta.sign = { dhash: dh, hist: hh };
        if (!p.meta.ts) p.meta.ts = getTimestampSec(p);
      } catch { /* ignore */ }
    }
  });
  await Promise.all(workers);
}

function cosine(a, b) {
  let dot=0, na=0, nb=0;
  for (let i=0;i<a.length;i++) { dot+=a[i]*b[i]; na+=a[i]*a[i]; nb+=b[i]*b[i]; }
  if (!na || !nb) return 0; return dot / (Math.sqrt(na)*Math.sqrt(nb));
}
export function photoSimilarity(a, b) {
  const sa = a?.meta?.sign, sb = b?.meta?.sign;
  if (!sa || !sb || !sa.dhash || !sb.dhash) return 0;
  const simHash = 1 - (hamming64(sa.dhash, sb.dhash) / 64);
  const simHist = (sa.hist && sb.hist) ? cosine(sa.hist, sb.hist) : 0;
  return clamp01(0.7 * simHash + 0.3 * simHist);
}

export function dedupeInOrder(photos, { threshold = 0.90, likedIds = new Set() } = {}) {
  const keep = [];
  for (const p of photos) {
    if (likedIds.has(p.id)) { keep.push(p); continue; }
    let dup = false;
    for (const q of keep) {
      if (photoSimilarity(p, q) >= threshold) { dup = true; break; }
    }
    if (!dup) keep.push(p);
  }
  return keep;
}

export function dedupeWithBursts(photos, {
  threshold = 0.92,
  burstWindowSec = 8,
  likedIds = new Set(),
} = {}) {
  if (!photos.length) return photos;
  const sorted = [...photos].sort((a,b) => (a?.meta?.ts||0) - (b?.meta?.ts||0));
  const keep = [];
  let i = 0;
  while (i < sorted.length) {
    const group = [sorted[i]];
    const t0 = sorted[i]?.meta?.ts || 0;
    let j = i + 1;
    while (j < sorted.length && ((sorted[j]?.meta?.ts || 0) - t0) <= burstWindowSec) {
      group.push(sorted[j]);
      j++;
    }
    // within the burst, keep highest-ranked (likes > score), drop near-dups
    const burstKept = [];
    for (const p of group) {
      if (likedIds.has(p.id)) { burstKept.push(p); continue; }
      let dup = false;
      for (const q of burstKept) {
        if (photoSimilarity(p, q) >= threshold) { dup = true; break; }
      }
      if (!dup) burstKept.push(p);
    }
    keep.push(...burstKept);
    i = j;
  }
  return keep;
}

/* ------------------------------ scoring ------------------------------- */

// Aggregate for multiple selected vibes using a balanced metric
function aggVibeBalanced(photo, selectedVibes) {
  const s = photo?.score || {};
  if (!selectedVibes?.length) return (s.people||0)+(s.scenery||0)+(s.activity||0)+(s.highlight||0);
  // use a mix of mean and min so images strong across all win
  let sum=0, minV=1, n=0;
  for (const v of selectedVibes) { const sv = clamp01(s[v]||0); sum += sv; minV = Math.min(minV, sv); n++; }
  const mean = n ? sum/n : 0;
  return 0.6*mean + 0.4*minV;
}

function aggVibe(photo, selectedVibes, weights) {
  // Back-compat average with optional per-vibe weights
  const s = photo?.score || {};
  const w = weights || { people:1, scenery:1, activity:1, highlight:1 };
  if (!selectedVibes?.length) {
    return (s.people||0) + (s.scenery||0) + (s.activity||0) + (s.highlight||0);
  }
  let sum=0, ww=0;
  for (const v of selectedVibes) {
    const sv = clamp01(s[v] || 0), wv=(w[v]||1);
    sum += wv * sv; ww += wv;
  }
  return ww ? sum/ww : sum;
}

function vibeStrictPass(photo, {
  selectedVibes = [],
  minVibeScore = 0.55,
  minPeopleScore = 0.60,
  requireDetectedPerson = false,
  requireAllSelectedVibes = false,
  likesBypass = true,
  liked = false,
} = {}) {
  if (likesBypass && liked) return true;
  const s = photo?.score || {};
  if (!selectedVibes.length) return true;

  if (requireAllSelectedVibes) {
    for (const v of selectedVibes) {
      const sv = s[v] || 0;
      if (v === 'people') { if (sv < minPeopleScore) return false; }
      else if (sv < minVibeScore) return false;
    }
  } else {
    let ok = false;
    for (const v of selectedVibes) {
      const sv = s[v] || 0;
      ok = ok || (v === 'people' ? sv >= minPeopleScore : sv >= minVibeScore);
    }
    if (!ok) return false;
  }

  if (requireDetectedPerson && selectedVibes.includes('people')) {
    const persons = photo?.meta?.person?.persons || 0;
    const cover = photo?.meta?.person?.coverage || 0;
    if (persons <= 0 || cover < 0.02) return false;
  }
  return true;
}

function rankPhoto(photo, { selectedVibes = [], likesBoost = 0.25 }) {
  const liked = !!photo._liked;
  const vsBalanced = aggVibeBalanced(photo, selectedVibes);
  const coverage = photo?.meta?.person?.coverage || 0;
  const persons  = photo?.meta?.person?.persons  || 0;
  const sceneConf = photo?.meta?.scene?.conf || 0;
  // Likes float to the top, then balanced vibe strength, then people coverage/boxes, then scene conf
  return (liked ? 10 : 0) + vsBalanced * 3 + coverage + Math.min(1, persons/3) + sceneConf * 0.5 + (liked ? likesBoost : 0);
}

/* -------------------------- diversification (MMR) ---------------------- */

function mmrDiversify(candidates, k, {
  lambda = 0.35,
  simFn = photoSimilarity,
  sceneKey = (p) => p?.meta?.scene?.label || '',
  perSceneCapFraction = 0.45,
  hardDedupThreshold = 0.96, // NEW: never allow items ≥ this similarity into the set
} = {}) {
  if (!candidates.length || k <= 0) return [];
  const out = [];
  const sceneCount = new Map();
  const cap = Math.max(1, Math.floor(perSceneCapFraction * k));

  while (out.length < k && out.length < candidates.length) {
    let bestIdx = -1, bestScore = -Infinity;

    for (let i=0;i<candidates.length;i++) {
      const c = candidates[i]; if (!c) continue;
      // hard skip if too similar to ANY already chosen
      let tooSimilar = false, maxSim = 0;
      for (const s of out) { const sim = simFn(c, s); maxSim = Math.max(maxSim, sim); if (sim >= hardDedupThreshold) { tooSimilar = true; break; } }
      if (tooSimilar) continue;

      const skey = sceneKey(c);
      const sc = sceneCount.get(skey) || 0;
      if (sc >= cap) continue; // hard per-scene cap

      const rel = c._rank ?? 0;
      let red = 0;
      for (const s of out) red = Math.max(red, simFn(c, s));
      const score = rel - lambda * red;

      if (score > bestScore) { bestScore = score; bestIdx = i; }
    }
    if (bestIdx === -1) break;
    const chosen = candidates[bestIdx];
    out.push(chosen);
    const skey = sceneKey(chosen);
    sceneCount.set(skey, (sceneCount.get(skey) || 0) + 1);
    candidates.splice(bestIdx, 1);
  }
  return out;
}

/* -------------------------- public API (used by UI) -------------------- */

export const DEFAULT_TEMPLATE_CATALOG = {
  grid4: { key: 'grid4', label: 'Grid x4', photosPerPage: 4 },
  grid6: { key: 'grid6', label: 'Grid x6', photosPerPage: 6 },
  collage: { key: 'collage', label: 'Collage', photosPerPage: 3 },
};

function applyLikes(results, likeMap = {}) {
  return results.map(r => ({ ...r, _liked: !!likeMap[r.id] }));
}

// Build the 2× candidate pool (semi-auto “Prep”)
export async function semiAutoCandidates(
  results,
  opts = {},
  multiplier = 2
) {
  const {
    selectedVibes = [],
    likeMap = {},
    likesBoost = 0.25,
    vibePolicy = 'strict',
    minVibeScore = 0.55,
    minPeopleScore = 0.60,
    requireDetectedPerson = false,
    requireAllSelectedVibes = false,
    likesBypass = true,
    maxPhotos = 20,
    softDedupThreshold = 0.95,
    diversityLambda = 0.45,
    perSceneCapFraction = 0.5,
    burstWindowSec = 8,            // NEW: time-burst dedupe window
  } = (opts || {});  // <-- make it null-safe

  const likedIds = new Set(Object.keys(likeMap || {}).filter(k => likeMap[k]));

  const base = applyLikes(results, likeMap)
    .filter(p => {
      if (vibePolicy !== 'strict') return true;
      return vibeStrictPass(p, {
        selectedVibes, minVibeScore, minPeopleScore,
        requireDetectedPerson, requireAllSelectedVibes, likesBypass,
        liked: p._liked
      });
    });

  await ensureSignatures(base);

  // soft dedupe first to shrink the pool
  const soft = dedupeWithBursts(base, { threshold: softDedupThreshold, burstWindowSec, likedIds });

  const scored = soft
    .map(p => ({ ...p, _rank: rankPhoto(p, { selectedVibes, likesBoost }) }))
    .sort((a,b) => b._rank - a._rank);

  const poolSize = Math.max(maxPhotos * multiplier, maxPhotos + 6);

  return mmrDiversify([...scored], poolSize, {
    lambda: diversityLambda,
    perSceneCapFraction,
    hardDedupThreshold: 0.97,
  });
}

// Finalize semi-auto with include/exclude/dedup/diversity
export async function finalizeSemiAuto(
  candidatePool,
  opts = {}
) {
  const {
    includeIds = [],
    excludeIds = [],
    selectedVibes = [],
    likesBoost = 0.25,
    maxPhotos = 20,
    dedup = { enabled: true, threshold: 0.90 },
    diversityLambda = 0.35,
    perSceneCapFraction = 0.45,
  } = (opts || {});

  const include = new Set(includeIds);
  const exclude = new Set(excludeIds);

  // Include > Exclude precedence
  const pool = candidatePool.filter(p => include.has(p.id) || !exclude.has(p.id));

  await ensureSignatures(pool);

  const likedIds = include; // treat included as liked/pinned
  const scored = pool
    .map(p => ({ ...p, _liked: likedIds.has(p.id), _rank: rankPhoto({ ...p, _liked: likedIds.has(p.id) }, { selectedVibes, likesBoost }) }))
    .sort((a,b) => b._rank - a._rank);

  const firstPass = dedup?.enabled
    ? dedupeInOrder(scored, { threshold: dedup.threshold ?? 0.90, likedIds })
    : scored;

  const diversified = mmrDiversify([...firstPass], maxPhotos, {
    lambda: diversityLambda,
    perSceneCapFraction,
    hardDedupThreshold: 0.97,
  });

  return {
    selected: diversified.slice(0, maxPhotos),
    diagnostics: { poolSize: pool.length, kept: diversified.length, excluded: exclude.size, included: include.size }
  };
}

// Full-auto path (used by “Generate (full auto)”)
// selection.js — replace selectAlbumPhotos with this version
export async function selectAlbumPhotos(results, opts = {}) {
  const {
    selectedVibes = [],
    likeMap = {},
    likesOnly = false,
    likesBoost = 0.25,
    maxPhotos = 20,
    dedup = { enabled: true, threshold: 0.90 },
    template = { photosPerPage: 4 },
    // strict defaults
    vibePolicy = 'strict',
    minVibeScore = 0.55,
    minPeopleScore = 0.60,
    requireDetectedPerson = false,
    requireAllSelectedVibes = false,
    likesBypass = true,
    backfillIfShort = true,
    // diversity
    diversityLambda = 0.35,
    perSceneCapFraction = 0.45,
    hardDedupThreshold = 0.97,
    // burst dedupe
    burstWindowSec = 8,
  } = (opts || {});

  const likedIds = new Set(Object.keys(likeMap || {}).filter(k => likeMap[k]));
  const applyPipeline = async (basePool) => {
    await ensureSignatures(basePool);
    const burstDeduped = dedupeWithBursts(basePool, {
      threshold: Math.max(0.92, dedup.threshold || 0.90),
      burstWindowSec, likedIds
    });
    const scored = burstDeduped
      .map(p => ({ ...p, _rank: rankPhoto(p, { selectedVibes, likesBoost }) }))
      .sort((a,b) => b._rank - a._rank);

    const pass1 = dedup?.enabled
      ? dedupeInOrder(scored, { threshold: dedup.threshold ?? 0.90, likedIds })
      : scored;

    let selected = mmrDiversify([...pass1], maxPhotos, {
      lambda: diversityLambda,
      perSceneCapFraction,
      hardDedupThreshold,
    });

    if (backfillIfShort && selected.length < maxPhotos) {
      const need = maxPhotos - selected.length;
      const remainder = pass1.filter(p => !selected.includes(p));
      selected = selected.concat(remainder.slice(0, need));
    }
    return { selected, burstDeduped, pass1 };
  };

  // 1) Start with base pool according to likes/strict policy
  let base = results.map(r => ({ ...r, _liked: !!likeMap[r.id] }));
  if (likesOnly) {
    base = base.filter(p => p._liked);
  } else if (vibePolicy === 'strict') {
    base = base.filter(p =>
      vibeStrictPass(p, {
        selectedVibes,
        minVibeScore,
        minPeopleScore,
        requireDetectedPerson,
        requireAllSelectedVibes,
        likesBypass,
        liked: p._liked
      })
    );
  }

  // 2) Try with strict settings
  let { selected, burstDeduped, pass1 } = await applyPipeline(base);
  if (selected.length >= maxPhotos || selectedVibes.length === 0 || likesOnly) {
    return {
      template,
      selected,
      diagnostics: { total: results.length, strictPool: base.length, burstDeduped: burstDeduped.length, deduped: pass1.length, final: selected.length, liked: likedIds.size }
    };
  }

  // 3) Progressive relax: lower thresholds & drop 'requireAll' if necessary
  let curMin = Math.min(minVibeScore, 0.55);
  let curMinPeople = Math.min(minPeopleScore, 0.60);
  let relaxSteps = [0.05, 0.1, 0.15, 0.2];
  let usedRequireAll = requireAllSelectedVibes;

  for (const step of relaxSteps) {
    const relaxV = Math.max(0.35, curMin - step);
    const relaxP = Math.max(0.45, curMinPeople - step);
    const relaxedPool = results.map(r => ({ ...r, _liked: !!likeMap[r.id] }))
      .filter(p => vibeStrictPass(p, {
        selectedVibes,
        minVibeScore: relaxV,
        minPeopleScore: relaxP,
        requireDetectedPerson,
        requireAllSelectedVibes: usedRequireAll && step < 0.1 ? true : false,
        likesBypass,
        liked: p._liked
      }));
    const attempt = await applyPipeline(relaxedPool);
    selected = attempt.selected;
    if (selected.length >= maxPhotos) {
      return {
        template,
        selected,
        diagnostics: {
          total: results.length,
          strictPool: base.length,
          relaxedPool: relaxedPool.length,
          final: selected.length,
          thresholds: { minVibeScore: relaxV, minPeopleScore: relaxP, requireAllSelectedVibes: usedRequireAll && step < 0.1 },
          liked: likedIds.size
        }
      };
    }
    // After first relax, stop requiring ALL selected vibes (use "at least one" above threshold)
    usedRequireAll = false;
  }

  // 4) If still short, just take the best of everything (with diversity), then backfill
  const fallbackPool = results.map(r => ({ ...r, _liked: !!likeMap[r.id] }));
  const final = await applyPipeline(fallbackPool);
  return {
    template,
    selected: final.selected,
    diagnostics: {
      total: results.length,
      strictPool: base.length,
      relaxedPool: 'fallback_all',
      final: final.selected.length,
      thresholds: 'fallback',
      liked: likedIds.size
    }
  };
}


// Safely convert a plan to a readable JSON blob (no BigInt errors)
export function albumPlanToBlobUrl(plan, opts = {}) {
  const { includeDebug = false } = opts;

  // Build a lighter, serializable view of the plan
  const safe = {
    summary: {
      createdAt: new Date().toISOString(),
      totalAnalyzed: plan?.stats?.total || plan?.all?.length || 0,
      selectedCount: plan?.selected?.length || 0,
      vibes: plan?.vibes || [],
      notes: plan?.notes || undefined,
    },
    selected: (plan?.selected || []).map(p => ({
      id: p.id ?? p.name ?? p.url ?? p.fileName,
      name: p.name ?? p.fileName ?? null,
      url: p.url ?? null,
      score: p.score ?? null,
      vibes: p.vibes ?? null,
      meta: p.meta ?? null,
      // Make signatures serializable but compact
      sig: p.sig ? {
        dhashHex: (typeof p.sig.dhash64 === 'bigint')
          ? '0x' + p.sig.dhash64.toString(16).padStart(16, '0')
          : (p.sig.dhashHex || null),
        // Histograms are large—omit by default
        hist: includeDebug ? p.sig.hist ?? null : undefined
      } : null,
      // If you carry GPS/time, keep them
      exif: p.exif ?? null,
      takenAt: p.takenAt ?? null,
      country: p.country ?? null,
      city: p.city ?? null
    }))
  };

  // Replacer converts any remaining BigInt anywhere in the object
  const replacer = (_k, v) => (typeof v === 'bigint' ? v.toString() : v);

  const json = JSON.stringify(safe, replacer, 2);
  const blob = new Blob([json], { type: 'application/json' });
  return URL.createObjectURL(blob);
}

